import { useCallback, useEffect, useRef, useState } from 'react';
import EphanDataService, { ARCHIVE_STATE } from 'services/EphanDataService';

const PAGE_SIZE = 24;

const FETCHERS = {
  dancers: (params) => EphanDataService.getDancers(params),
  coaches: (params) => EphanDataService.getCoaches(params),
  judges: (params) => EphanDataService.getJudges(params),
  clubs: (params) => EphanDataService.getClubs(params),
};

/**
 * Сторінка реєстру: перелік, пошук і довантаження — ADR-0015.
 *
 * Було: чотири майже однакові зрізи сховища (танцюристи, тренери, судді, клуби)
 * по сто тридцять рядків кожен, плюс п'ятий зі списком статусів, який нічого не
 * питав і слугував замком: доки він «не завантажився», решта не починала
 * вантажитися. Стан жив у сховищі, хоч читала його одна сторінка, і той стан
 * доводилося скидати руками при кожному переході.
 *
 * Стало: стан живе тут, разом зі сторінкою, що його показує. Сховище лишається
 * за розділами CMS, які справді читають кілька сторінок.
 *
 * Реєстр і архів — не наша вибірка, а два розділи джерела: архів питається
 * `registryState=ARCHIVE`, реєстр не питає нічого й отримує типове
 * `REGISTRY,BUFFER`.
 *
 * @param {'dancers'|'coaches'|'judges'|'clubs'} kind
 * @param {{ archive?: boolean }} options
 */
export function useRegistryList(kind, { archive = false } = {}) {
  const [state, setState] = useState({
    items: [],
    total: 0,
    page: 1,
    hasMore: false,
    status: 'idle',
    errors: [],
    isLoadingMore: false,
  });
  const [filters, setFilters] = useState({});

  // Кожен запит має номер: відповідь на скасований (перемкнули розділ, натиснули
  // «Скинути») не має лягти в стан замість свіжої.
  const requestId = useRef(0);

  // Дзеркало стану поза React: спостерігач за прокруткою кличе loadMore не з
  // обробника події React, тож React відкладає застосування функції-оновлювача
  // setState — читати з неї відразу після виклику небезпечно, значення ще
  // старе. Ref завжди синхронний, тому і застава від подвійного запиту, і
  // номер наступної сторінки беруться звідси, а не з setState.
  const stateRef = useRef(state);

  const applyState = (next) => {
    stateRef.current = next;
    setState(next);
  };

  const fetcher = FETCHERS[kind];

  const load = useCallback(
    async (nextFilters = {}) => {
      if (!fetcher) return;

      const id = ++requestId.current;
      setFilters(nextFilters);
      applyState({ ...stateRef.current, status: 'loading', errors: [] });

      const { result, total, page, hasNextPage, errors } = await fetcher({
        ...nextFilters,
        registryState: archive ? ARCHIVE_STATE : undefined,
        page: 1,
        limit: PAGE_SIZE,
      });

      if (id !== requestId.current) return;

      if (errors.length > 0) {
        applyState({ items: [], total: 0, page: 1, hasMore: false, status: 'failed', errors, isLoadingMore: false });
        return;
      }

      applyState({
        items: result,
        total,
        page,
        hasMore: hasNextPage,
        status: 'succeeded',
        errors: [],
        isLoadingMore: false,
      });
    },
    [fetcher, archive]
  );

  const loadMore = useCallback(async () => {
    if (!fetcher) return;

    const current = stateRef.current;
    if (current.isLoadingMore || !current.hasMore) return;

    const nextPage = current.page + 1;
    applyState({ ...current, isLoadingMore: true });

    const id = requestId.current;
    const { result, total, page, hasNextPage, errors } = await fetcher({
      ...filters,
      registryState: archive ? ARCHIVE_STATE : undefined,
      page: nextPage,
      limit: PAGE_SIZE,
    });

    if (id !== requestId.current) return;

    // Збій довантаження не стирає показане: лишається те, що вже приїхало.
    if (errors.length > 0) {
      applyState({ ...stateRef.current, isLoadingMore: false, errors });
      return;
    }

    applyState({
      ...stateRef.current,
      items: [...stateRef.current.items, ...result],
      total,
      page,
      hasMore: hasNextPage,
      isLoadingMore: false,
    });
  }, [fetcher, archive, filters]);

  // Зміна розділу чи перехід у архів — це новий перелік із чистими фільтрами.
  useEffect(() => {
    load({});
  }, [load]);

  const reset = useCallback(() => load({}), [load]);
  const retry = useCallback(() => load(filters), [load, filters]);

  return {
    items: state.items,
    total: state.total,
    hasMore: state.hasMore,
    status: state.status,
    errors: state.errors,
    isLoading: state.status === 'idle' || state.status === 'loading',
    isLoadingMore: state.isLoadingMore,
    isFiltered: Object.keys(filters).length > 0,
    search: load,
    loadMore,
    reset,
    retry,
  };
}
